/*!
// <copyright file="AppOverrides.js" company="Microsoft">
//     Copyright (c) Microsoft Corporation.  All rights reserved.
// </copyright>
*/

import { appletInteraction } from "../AppletInteraction";
import { app } from "../DynamicsApp";
import { powerApps } from "./PowerApps";
import { messaging } from "@ax/office-shared-lib";
import { powerAppsLabelLoader } from "@ax/office-cdn-app-resources";
import { LabelLoader as BaseLabelLoader } from "@ax/office-resources-common";

/* global app, appletInteraction, powerApps, resources, resourcesOverride */

/** Encapsulates the logic necessary for overridding functionality for PowerApps. */
export namespace appOverrides {

    /** Provides mechanisms for overriding functionality for PowerApps. */
    export class AppOverrides {

        /** The original 'appletInteraction.sendMessageToResource' method. */
        private originalSendMessageToResource = appletInteraction.sendMessageToResource;

        /** The original 'app._executeConfiguration' method. */
        private originalExecuteConfiguration = app._executeConfiguration;

        /** The original 'app.postInitialization' method. */
        private originalPostInitialization = app.postInitialization;

        constructor() {
            this.sendMessageToResource = this.sendMessageToResource.bind(this);
            this.overrideApp = this.overrideApp.bind(this);
            this.executeConfiguration = this.executeConfiguration.bind(this);
            this.postInitialization = this.postInitialization.bind(this);
        }

        /**
         * Sends the provided message to the resource corresponding to the provided resource ID, first massaging messages per any PowerApps specific logic.
         * @param resourceID The ID of the resource to send the provided message to.
         * @param message The message to send to the resource.
         * @param cloneData Indicates if data should be cloned; defaults to true.
         */
        public sendMessageToResource(resourceID: string, message: messaging.BaseRequest, cloneData?: boolean): void {
            powerApps.massageResourceRequests(resourceID, message);
            this.originalSendMessageToResource(resourceID, message, cloneData);
        }


        /** Executes the configuration retrieval process, and adds PowerApps specific options. */
        public executeConfiguration(): void {
            powerApps._executeConfiguration(this.originalExecuteConfiguration);
        }

        /** Performs post initialization work, beginning the authentication flow appropriately, and adds PowerApps specific options. */
        public postInitialization(): void {
            powerApps.initialized(this.originalPostInitialization);
        }

        /** Gets the label loader to use in the app */
        public getLabelLoader(): BaseLabelLoader {
            return powerAppsLabelLoader;
        }

        /**
         * Gets the name of the application CSS file
         * @param styleSheetNamePrefix Name of the Office application to load
         */
        public getAppCssName(styleSheetNamePrefix: string): string {
            // eslint-disable-next-line security/detect-non-literal-require -- variable is checked before the function call (not user controlled input)
            return require(`./Styles/${styleSheetNamePrefix}PowerApps.less`);
        }

        /** Overrides the app functionality with PowerApps appropriate functionality. */
        public overrideApp(): void {

            // Adjust the path to root to account for the extra PowerApps directory
            app.pathToRoot = "../../";

            // Adjust the format used to determine the add-in stylesheet
            app.addinStylesheetFormat = "App/PowerApps/{0}PowerApps.css";

            // Override resources for PowerApps
            // for (let resourceProperty in resourcesOverride) {
            //     resources[resourceProperty] = resourcesOverride[resourceProperty];
            // }

            // Override appropriate methods
            appletInteraction.sendMessageToResource = this.sendMessageToResource;
            app.postInitialization = this.postInitialization;
            app._executeConfiguration = this.executeConfiguration;
            app.getLabelLoader = this.getLabelLoader;
            app.getAppCssName = this.getAppCssName;
        }
    }

    export const overrides = new AppOverrides();
}
